import md5 from 'js-md5';

const StorageTokenKey = `_token_${window.SERVER_CONFIG.APP_ENV}_`;

/**
 * 读/写本地缓存token字符串
 * @param {string} str token字符串
 * @return {string}
 */
export function token(str?: string): string {
    if (str) {
        localStorage.setItem(StorageTokenKey, str);
        return str;
    } else {
        return localStorage.getItem(StorageTokenKey) || '';
    }
}

/**
 * 清除token缓存
 */
export function tokenClear(): void {
    localStorage.removeItem(StorageTokenKey);
}

/**
 * 获取本地缓存token内用户信息
 * @return {object}
 */
export function tokenParse(): Record<string, any> | null {
    const userStr: string = token().split('.')[0] || '';
    let obj;

    if (userStr) {
        obj = JSON.parse(window.atob(userStr));
    } else {
        obj = null;
    }

    return obj;
}

/**
 * 校验token时效
 * @description 校验浏览器类型，H5默认返回true
 */
export function tokenCheck(): boolean {
    if (userAgent() === 'H5') {
        return true;
    } else {
        const tokenData = tokenParse();
        if (tokenData) {
            const now = Math.round(new Date().getTime() / 1000);
            const exp = !!(tokenData.expiration > now + 600); // 提前10分钟

            if (userAgent() === 'Wechat') {
                return !!tokenData.openid && exp;
            } else {
                return exp;
            }
        } else {
            return !!tokenData;
        }
    }
}

/**
 * base64编码
 * @param {string} str
 * @return {string}
 */
export function encodeBase64(str: string): string {
    return window.btoa(encodeURIComponent(str));
}

/**
 * base64解码
 * @param {string} str
 * @return {string}
 */
export function decodeBase64(str: string): string {
    return window.atob(decodeURIComponent(str));
}

/**
 * 读取客户端类型
 * @return {string}
 */
export function userAgent(): string {
    if (navigator.userAgent.match('MicroMessenger')) {
        return 'Wechat';
    } else if (navigator.userAgent.match(/(Alipay)|(alipay)/)) {
        if (navigator.userAgent.match(/(miniprogram)|(MiniProgram)/)) {
            return 'AlipayMiniProgram';
        }
        return 'Alipay';
    } else {
        return 'H5';
    }
}

/**
 * 时间格式转换
 * 约定: 非单字母补零，区分大小写
 *  'yyyy' { 年 | - | / }
 *  'M|MM' { 月 | - | / }
 *  'd|dd' { 日 | - | / }
 *  'h|hh' { 时 | : }
 *  'm|mm' { 分 | : }
 *  's|ss' { 秒 | : }
 *  e.g. 'yyyy年MM月dd日 hh:mm:ss' -> '2018年10月09日 09:58:02'
 * @param {[Date, String, Number]} val Date对象|时间戳(s|ms)|字符串yyyy-MM-dd此类
 * @param {string} format 时间格式
 * @return {string}
 */
export function datetimeFormatter(val: string | number | Date, format: string = 'yyyy-MM-dd hh:mm:ss'): any {
    if (!val) {
        return val;
    }

    let date: Date;
    let result = '';

    if (val.constructor === Date) {
        date = new Date(val.valueOf());
    } else if (val.constructor === String) {
        const valNum = Number(val);

        if (isNaN(valNum)) {
            date = new Date(val);
        } else {
            // timestamp  length > 10: unit is 's', else unit is 'ms'
            date = new Date(valNum * ((val as string).length > 10 ? 1 : 1000));
        }
    } else if (val.constructor === Number) {
        const valStr = val + '';
        date = new Date((val as number) * (valStr.length > 10 ? 1 : 1000));
    } else {
        throw new Error('function \'datetimeFormatter\' except param [Date, Number, String]');
    }

    // 补零
    function paddingZero(num: number): string {
        return num < 10 ? ('0' + num) : num.toString();
    }

    // format 'yyyy'
    result = format.replace('yyyy', date.getFullYear().toString());

    // format 'M|MM'
    const month = date.getMonth() + 1;
    if (format.search('MM') !== -1) {
        result = result.replace('MM', paddingZero(month));
    } else if (format.search(/M{1}/)) {
        result = result.replace('M', month.toString());
    }

    // format 'd|dd'
    const day = date.getDate();
    if (format.search('dd') !== -1) {
        result = result.replace('dd', paddingZero(day));
    } else if (format.search(/d{1}/)) {
        result = result.replace('d', day.toString());
    }

    // format 'h|hh'
    const hours = date.getHours();
    if (format.search('hh') !== -1) {
        result = result.replace('hh', paddingZero(hours));
    } else if (format.search(/h{1}/)) {
        result = result.replace('h', hours.toString());
    }

    // format 'm|mm'
    const minutes = date.getMinutes();
    if (format.search('mm') !== -1) {
        result = result.replace('mm', paddingZero(minutes));
    } else if (format.search(/m{1}/)) {
        result = result.replace('m', hours.toString());
    }

    // format 's|ss'
    const seconds = date.getSeconds();
    if (format.search('ss') !== -1) {
        result = result.replace('ss', paddingZero(seconds));
    } else if (format.search(/s{1}/)) {
        result = result.replace('s', seconds.toString());
    }

    return result;
}


/**
 * 数组操作
 */
export const ArrayExpand = {
    getValuesByKey(data: any[], key: any): any[] {
        if (!data.length) {
            return data;
        }
        return data.map((item: any) => data[key]);
    },

    getValuesByKeyUnique(data: any[], key: any): any[] {
        if (!data.length) {
            return data;
        }
        const result = new Set();
        data.forEach((item: any) => {
            result.add(item[key]);
        });
        return Array.from(result);
    },

    /**
     * 单值分组
     * @param {Array} data 数据
     * @param {String} column 分组字段
     * @param {[String, Function]} sort<String> 分组排序['desc', 'asc'] 默认不排序
     * @param {[String, Function]} sort<Function> 排序方法，与Array.sort(sortby)相同 in future
     * @return {Array}
     */
    groupBy(
        data: any[],
        column: any,
        sort?: (...args: any) => any | string,
        ...args: any
    ): any[] | Record<string, any> {
        if (data.length <= 1) {
            return arguments[3] === true
                ? {
                    result: data,
                    columns: data.length ? [data[column]] : [],
                    groups: [data],
                }
                : data;
        }

        // 分组值
        const columns = ArrayExpand.getValuesByKeyUnique(data, column);

        // 分组值内 排序
        if (sort?.toString() === 'asc') {
            columns.sort((a: any, b: any) => {
                return a - b;
            });
        } else if (sort?.toString() === 'desc') {
            columns.sort((a: any, b: any) => {
                return b - a;
            });
        } else if (typeof sort === 'function') {
            columns.sort(sort);
        }

        // 数据按组过滤
        const groups = columns.map((item: any) => data.filter((v: any) => v[column] === item));

        // 分组数据合并
        const result = groups.concat();
        return args[0] === true // groupByAll再封装使用，返回完整筛选数据
            ? { result, groups, columns }
            : result;
    },

    /** 返回单值分组的完整数据 */
    groupByAll(
        data: any[],
        column: any,
        sort?: (...args: any) => any | string,
    ) {
        return ArrayExpand.groupBy.call(this, data, column, sort, true);
    },
};


/**
 * 优惠券展示字段处理
 */
export function handleCoupon(data: any[]): any[] {
    const result = data.concat();

    result.forEach((item: any) => {
        if (item.c_type !== 2) {
            item.amountNum = [0, 3].includes(item.c_type)
                ? Number(item.amount)
                : item.c_type === 4
                    ? Math.round(Number(item.amount) * 10) / 100
                    : Math.round(Number(item.amount) / 60 * 100) / 100;

            // 小数拆分
            if (item.amount % 1 === 0) {
                item.amountStr = [item.amountNum];
            } else {
                item.amountStr = [String(item.amountNum).split('.')[0], '.', String(item.amountNum).split('.')[1]];
            }

            // 根据长度设置字体大小
            const strLen = String(item.amountNum).length;
            item.amountSize = strLen === 6
                ? 'small'
                : (strLen <= 5 && strLen >= 3) ? 'middle' : 'large';
        }

        const now = new Date();
        if (!item.expire_minutes_to_use) { // 当天有效
            const end = new Date(item.get_time * 1000);

            end.setHours(23);
            end.setMinutes(59);
            end.setSeconds(59);

            // 有效期
            item.usefulStr = datetimeFormatter(item.get_time, 'yyyy-MM-dd') + ' 23:59:59';
            item.expireStr = datetimeFormatter(item.get_time, 'yyyy-MM-dd');
            // 截止日期
            item.endStr = datetimeFormatter(end);
            // 距离过期剩余的毫秒
            item.deadTime = end.getTime() - now.getTime();
        } else {
            const start = datetimeFormatter(item.get_time, 'yyyy-MM-dd');
            const expire = item.get_time + item.expire_minutes_to_use * 60;
            const end = datetimeFormatter(expire, 'yyyy-MM-dd');

            // 完整有效期
            item.usefulStr = start + '至' + end;
            // 截止日期
            item.endStr = datetimeFormatter(expire);
            item.expireStr = datetimeFormatter(expire);
            // 距离过期剩余的毫秒
            item.deadTime = expire * 1000 - now.getTime();
        }
    });

    return result;
}

/**
 * 参数首字母升序加密算法
 * @param {any} params
 * @param {string} key
 * @param {boolean} doURIencode 对参数进行uri转义
 */
export function sign(params: any, key: string, doURIencode: boolean = true): string {
    const paramsKey = params.map((item: any, index: any) => index);
    paramsKey.sort();

    let signStr = '';
    for (const i in paramsKey) {
        if (params[paramsKey[i]] === '' || params[paramsKey[i]] === null) {
            continue;
        } else {
            if (doURIencode) {
                signStr += `${paramsKey[i]}=${encodeURIComponenPlus(params[paramsKey[i]])}&`;
            } else {
                signStr += `${paramsKey[i]}=${params[paramsKey[i]]}&`;
            }
        }
    }
    signStr += `key=${key}`;

    return md5(signStr);
}

/**
 * url转义
 * js原生encodeURIComponent()不对 ~!*() 进行转义
 * 该方法完善了上述字符的转义
 * @param {*} str
 */
export function encodeURIComponenPlus(str: string): string {
    str = (str + '').toString();

    return encodeURIComponent(str)
        .replace(/!/g, '%21')
        .replace(/'/g, '%27')
        .replace(/\(/g, '%28')
        .replace(/\)/g, '%29')
        .replace(/\*/g, '%2A')
        .replace(/%20/g, '+');
}

/**
 * 解析出口码中的车场ID
 * 出口码格式 rryb-{parkId}-{出口设备号}（权威定义见 gate-plate 项目 constants/constants.go，
 * 闸机出场事件用 ParkNumber+ExitDeviceNumber 拼 key 写 redis，出口二维码即编码该值）
 * @param {*} plate URL 的 plate 参数（或任意字符串）
 * @return {number|null} 车场ID；非出口码格式返回 null
 * @author logic
 * @date 2026-10-09
 */
export function parseExitParkId(plate: any): number | null {
    if (typeof plate !== 'string') {
        return null;
    }
    const match = /^rryb-(\d+)-\d+/.exec(plate);
    return match ? +match[1] : null;
}

export { default as ScaleHtmlFontSize } from '@/assets/ts/ScaleHtmlFontSize';
